/* eslint-disable max-len */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import '../../styles/LoginPage.css';
import BdoLoginLogo from '../../assets/images/BDO-logo-large.png';
import BdoLoginLogoSmall from '../../assets/images/BDO-logo-small.png';
import appConfig from '../../config/appConfig';
import buildInfo from '../../buildInfo.json';

const normalizeVanity = (value) => {
  const vanity = (value || '').trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(vanity) ? vanity : '';
};

const DISCOVER_ERROR_MESSAGES = {
  IDENTIFIER_REQUIRED: 'Please enter your work email or phone number.',
  USER_NOT_FOUND: 'This identifier is not registered.',
  TENANT_LOGIN_MISMATCH: 'This identifier is not registered for this sign-in link.',
  DB_UNAVAILABLE: 'We could not reach the directory. Please try again in a moment.',
  LOOKUP_FAILED: 'Something went wrong looking you up. Please try again.',
  UNEXPECTED_RESPONSE: 'Unexpected response from server.',
};

const errorMessageFor = (code) => (
  DISCOVER_ERROR_MESSAGES[code] || DISCOVER_ERROR_MESSAGES.LOOKUP_FAILED
);

const buildAuthLoginUrl = (vanity, { tHorne = false, loginHint = '' } = {}) => {
  let url = `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/login?vanity=${encodeURIComponent(vanity)}`;
  if (tHorne) url += '&t=horne';
  if (loginHint) url += `&login_hint=${encodeURIComponent(loginHint)}`;
  return url;
};

const BrandedSignIn = ({
  vanity, branding, loginHint = '', tHorne = false,
}) => {
  const logo = branding?.logoUrl || BdoLoginLogo;
  const displayName = branding?.displayName || appConfig.appName;
  const subtitle = branding?.loginCopy || `Sign in to continue to ${displayName}.`;

  return (
    <div className="LoginPageContainer">
      <div className="container">
        <div className="info-section">
          <img
            src={logo}
            alt={`${displayName} login logo`}
            className="HorneLoginLogo"
          />
          <h1 className="appNameLogin">{displayName}</h1>
          <p>{appConfig.loginDescription}</p>
        </div>

        <div className="login-section">
          <div className="login-box">
            {!branding?.logoUrl && (
              <img src={BdoLoginLogoSmall} alt={`${appConfig.companyName} form logo`} className="loginBoxLogo" />
            )}
            <h2 className="login-title">Welcome to the Intelligent Virtual Assistant</h2>
            <p className="login-subtitle">{subtitle}</p>

            <a
              href={buildAuthLoginUrl(vanity, { tHorne, loginHint })}
              className="login-button"
              data-testid="branded-signin-button"
            >
              Sign in
            </a>

            <div className="login-footer">
              <span className="build-version">
                v
                {buildInfo.version}
                {' '}
                &middot;
                {' '}
                {buildInfo.buildDate}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const readDiscoverParams = () => {
  if (typeof window === 'undefined' || !window.location) {
    return { error: null, identifier: '' };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    error: params.get('error'),
    identifier: params.get('identifier') || '',
  };
};

const ColdLookupForm = () => {
  const initial = readDiscoverParams();
  const [input, setInput] = useState(initial.identifier);
  const [clientError, setClientError] = useState(null);
  const [serverError, setServerError] = useState(initial.error);

  useEffect(() => {
    if (!initial.error || typeof window === 'undefined') return;
    const cleaned = new URL(window.location.href);
    cleaned.searchParams.delete('error');
    cleaned.searchParams.delete('identifier');
    window.history.replaceState({}, '', cleaned.pathname + cleaned.search + cleaned.hash);
  }, [initial.error]);

  const isValidInput = (value) => (
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    || /^\+?\d{1,4}[-\s]?\(?\d+\)?([-.\s]?\d+)*$/.test(value)
  );

  const handleSubmit = (event) => {
    if (!isValidInput(input)) {
      event.preventDefault();
      setClientError('Please enter a valid email or phone number.');
      return;
    }
    setClientError(null);
    setServerError(null);
    // Let the browser submit the form natively — the backend will 303
    // redirect us either to the branded signin page or back here with
    // ?error=<code>.
  };

  const error = clientError || (serverError ? errorMessageFor(serverError) : null);

  return (
    <div className="LoginPageContainer">
      <div className="container">
        <div className="info-section">
          <img
            src={BdoLoginLogo}
            alt={`${appConfig.companyName} Login Logo`}
            className="HorneLoginLogo"
          />
          <h1 className="appNameLogin">{appConfig.appName}</h1>
          <p>{appConfig.loginDescription}</p>
        </div>

        <div className="login-section">
          <form
            method="POST"
            action={`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/discover`}
            onSubmit={handleSubmit}
            data-testid="cold-lookup-form"
          >
            <div className="login-box">
              <img src={BdoLoginLogoSmall} alt={`${appConfig.companyName} Form Logo`} className="loginBoxLogo" />
              <h2 className="login-title">Welcome to the Intelligent Virtual Assistant</h2>
              <p className="login-subtitle">Your single gateway to all your workspaces</p>

              <div className="form-group">
                <label htmlFor="emailInput" className="login-label">
                  Enter your work email or phone number
                </label>
                <input
                  id="emailInput"
                  name="identifier"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="e.g. john.doe@acme.com or +1-555-123-4567"
                  className={`login-input ${error ? 'input-error' : ''}`}
                  aria-describedby={error ? 'emailError' : undefined}
                  aria-invalid={!!error}
                />
                {error && <div id="emailError" className="error-message">{error}</div>}
              </div>

              <button
                className="login-button"
                disabled={!input.trim()}
                type="submit"
                data-testid="cold-lookup-next"
              >
                Next
              </button>

              <div className="login-footer">
                Trouble logging in? Contact your organization&apos;s administrator.
                <span className="build-version">
                  v
                  {buildInfo.version}
                  {' '}
                  &middot;
                  {' '}
                  {buildInfo.buildDate}
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const LoginPage = ({
  requestedVanity = '', branding = null, loginHint = '', tHorne = false,
}) => {
  const vanity = normalizeVanity(requestedVanity);
  return vanity
    ? <BrandedSignIn vanity={vanity} branding={branding} loginHint={loginHint} tHorne={tHorne} />
    : <ColdLookupForm />;
};

export default LoginPage;
